import { Button } from '@listening/frontend/components/radix/button';
import { CtaButton } from '@/components/astro/cta-button/cta-button.tsx';
import type { ReactNode } from 'react';

interface HeroProps {
  title: string;
  description: string;
  buttonLabel: string;
  buttonHref?: string;
  badge?: string;
  children?: ReactNode;
}

export function Hero({
  title,
  description,
  buttonLabel,
  buttonHref,
  badge,
  children,
}: HeroProps) {
  return (
    <div
      className='mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8'
      itemScope
      itemType='http://schema.org/WebPageElement'
    >
      <section
        aria-labelledby='hero-heading'
        className='flex flex-col items-center justify-center pt-32 pb-8 text-center sm:pt-40 sm:pb-12 md:pt-48 lg:pt-40 lg:pb-24'
      >
        {badge && (
          <div className='mb-4'>
            <span className='inline-flex items-center rounded-full bg-blue-100 px-4 py-1.5 text-sm font-medium text-blue-800'>
              {badge}
            </span>
          </div>
        )}
        <h1
          className='mb-4 max-w-xs text-3xl leading-[1.1] font-black sm:mb-6 sm:max-w-2xl sm:text-5xl md:max-w-4xl md:text-6xl lg:text-8xl'
          id='hero-heading'
          itemProp='headline'
        >
          {title}
        </h1>
        <p
          className='mb-6 max-w-sm text-base font-semibold text-neutral-665 sm:mb-8 sm:max-w-lg sm:text-lg lg:max-w-2xl lg:text-[20px]'
          itemProp='description'
        >
          {description}
        </p>
        <div aria-label='Get started' className='relative z-10' role='group'>
          {buttonHref ? (
            <Button
              asChild
              className='rounded-full bg-royal-50 px-8 py-7 text-xl font-light text-white hover:bg-black/80 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden'
            >
              <a href={buttonHref}>{buttonLabel}</a>
            </Button>
          ) : (
            <CtaButton
              className='rounded-full bg-royal-50 px-8 py-7 text-xl font-light text-white hover:bg-black/80 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden'
              label={buttonLabel}
              variant='gradient'
            />
          )}
        </div>
        {children}
      </section>
    </div>
  );
}
